// Standalone, read-only, public sport club home page rendered for share links
// (`/club/:token`). It mounts OUTSIDE the auth provider (see `main.tsx`), so a
// visitor never sees a login screen — the opaque home token in the URL is the
// only capability. Every company has a public home page; the server returns a
// narrow projection with no company id and no coach personal numbers. Each
// listed coach links to their own public profile page (`/p/:token`) and each
// tournament to its public spectator view (`/view/:id`).
//
// The layout is responsive: a single mobile-first column that widens on desktop,
// with the training week-calendar and photo gallery spanning the full width and
// the coaches / tournaments laid out as card grids. Visual style matches the
// public spectator and profile views: a gradient hero over frosted, glowing
// cards (see `publicStyles`).

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SportsIcon from '@mui/icons-material/Sports';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PlaceIcon from '@mui/icons-material/Place';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from '../../state/I18nContext';
import { LanguageSelector } from '../LanguageSelector';
import { PhotoGallery } from '../PhotoGallery';
import { Markdown } from '../Markdown';
import type { PublicClubCoach } from '../../domain';
import { ClubWeekCalendar } from './ClubWeekCalendar';
import { useClubHome } from './useClubHome';
import { publicProfilePath } from './profileSharePath';
import { publicViewPath } from './sharePath';
import { ACCENT_GRADIENT, HERO_GRADIENT, glowCard, rise } from './publicStyles';

interface PublicClubViewProps {
  clubToken: string;
}

// Card grid used for the coaches and tournaments lists: one column on phones,
// two from the small breakpoint up so the wider desktop layout is filled. The
// tracks use `minmax(0, 1fr)` (never a bare `1fr`, which is `minmax(auto, 1fr)`
// and would grow past the container to fit non-wrapping text).
const cardGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
  gap: 1.5,
} as const;

const linkCard = {
  ...glowCard(),
  p: 1.5,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  textDecoration: 'none',
  color: 'inherit',
  transition: 'transform .15s ease',
  '&:hover': { transform: 'translateY(-2px)' },
} as const;

/**
 * One coach card in the Coaches grid. The whole card links to the coach's public
 * profile; the bio is clamped to two lines so a long one never blows out the
 * card (or, in the two-column grid, its whole row), with a "More" cue — shown
 * only when the text is actually truncated — inviting a click through to the
 * full profile.
 */
function CoachCard({ coach }: { coach: PublicClubCoach }) {
  const { t } = useTranslation();
  const bioRef = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);

  // "More" appears only when the clamped bio is actually cut off; re-checked on
  // resize since the card width (and so the line count) changes with breakpoint.
  useEffect(() => {
    const el = bioRef.current;
    if (!el) return;
    const measure = () => setTruncated(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [coach.description]);

  return (
    <Paper
      component="a"
      href={publicProfilePath(coach.profileToken)}
      aria-label={t('club.viewProfile', { name: coach.displayName })}
      sx={{ ...linkCard, alignItems: 'flex-start' }}
    >
      <Avatar
        src={coach.photoUrl ?? undefined}
        alt={coach.displayName}
        sx={{ flexShrink: 0, backgroundImage: coach.photoUrl ? undefined : ACCENT_GRADIENT }}
      >
        <SportsIcon />
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap>
          {coach.displayName}
        </Typography>
        {coach.description && (
          <>
            <Typography
              ref={bioRef}
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {coach.description}
            </Typography>
            {truncated && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                {t('club.more')}
              </Typography>
            )}
          </>
        )}
      </Box>
      <ChevronRightIcon color="disabled" sx={{ flexShrink: 0, alignSelf: 'center' }} />
    </Paper>
  );
}

export function PublicClubView({ clubToken }: PublicClubViewProps) {
  const { t } = useTranslation();
  const { home, status, reload } = useClubHome(clubToken);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundImage: HERO_GRADIENT }}>
        <Container maxWidth="md" sx={{ pt: 2, pb: 1 }}>
          <Stack direction="row" justifyContent="flex-end">
            <LanguageSelector />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 6 }}>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {(status === 'notfound' || status === 'error') && (
          <Paper sx={{ ...glowCard(), p: 4, mt: 4, textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
            <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {t(status === 'notfound' ? 'club.notFound.title' : 'club.error.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(status === 'notfound' ? 'club.notFound.body' : 'club.error.body')}
            </Typography>
            {status === 'error' && (
              <Button startIcon={<RefreshIcon />} onClick={reload}>
                {t('club.retry')}
              </Button>
            )}
          </Paper>
        )}

        {status === 'ready' && home && (
          <Stack spacing={3} sx={{ mt: 2, animation: `${rise} .4s ease both` }}>
            {/* Hero: logo, name, location. */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                variant="rounded"
                src={home.logoUrl ?? undefined}
                alt={home.name}
                sx={{
                  width: { xs: 88, md: 104 },
                  height: { xs: 88, md: 104 },
                  backgroundImage: home.logoUrl ? undefined : ACCENT_GRADIENT,
                }}
              >
                <SportsIcon sx={{ fontSize: 44 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {home.name}
                </Typography>
                {home.locationName && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <PlaceIcon fontSize="small" color="disabled" />
                    <Typography variant="body1" color="text.secondary">
                      {home.locationName}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Stack>

            {/* Description (markdown). */}
            {home.description && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('club.about')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Markdown>{home.description}</Markdown>
                </Box>
              </Paper>
            )}

            {/* Training: the week schedule planner. The cards carry each
                class's description, audience and linked coaches, so no
                separate class list follows. */}
            {home.training.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('club.training')}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <ClubWeekCalendar training={home.training} />
                </Box>
              </Paper>
            )}

            {/* Photos. */}
            {home.photos.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('club.photos')}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <PhotoGallery
                    urls={home.photos.map((photo) => photo.url)}
                    alt={home.name}
                    emptyLabel={t('club.noPhotos')}
                    labels={{
                      close: t('gallery.close'),
                      download: t('gallery.download'),
                      previous: t('gallery.previous'),
                      next: t('gallery.next'),
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* Coaches, each linking to their public profile page. */}
            {home.coaches.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('club.coaches')}
                </Typography>
                <Box sx={{ ...cardGrid, mt: 1 }}>
                  {home.coaches.map((coach, i) => (
                    <CoachCard key={i} coach={coach} />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Tournaments the club has organised, linking to their public views.
                A DRAFT still taking sign-ups is flagged so visitors can register. */}
            {home.tournaments.length > 0 && (
              <Paper sx={{ ...glowCard(), p: 3 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('club.tournaments')}
                </Typography>
                <Box sx={{ ...cardGrid, mt: 1 }}>
                  {home.tournaments.map((tournament) => (
                    <Paper
                      key={tournament.id}
                      component="a"
                      href={publicViewPath(tournament.id)}
                      aria-label={t('club.viewTournament', { name: tournament.name })}
                      sx={{
                        ...linkCard,
                        ...(tournament.registrationOpen
                          ? { borderColor: 'success.main' }
                          : {}),
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={tournament.coverUrl ?? undefined}
                        alt={tournament.name}
                        sx={{
                          backgroundImage: tournament.coverUrl ? undefined : ACCENT_GRADIENT,
                        }}
                      >
                        <EmojiEventsIcon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1">
                          {tournament.name}
                        </Typography>
                        <Chip
                          size="small"
                          variant={tournament.registrationOpen ? 'filled' : 'outlined'}
                          color={tournament.status === 'FINISHED' ? 'default' : 'success'}
                          label={t(`club.tournamentStatus.${tournament.status}`)}
                          sx={{ mt: 0.25 }}
                        />
                      </Box>
                      {tournament.registrationOpen ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          component="span"
                          sx={{ flexShrink: 0 }}
                        >
                          {t('club.registerCta')}
                        </Button>
                      ) : (
                        <ChevronRightIcon color="disabled" />
                      )}
                    </Paper>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Empty club: no content sections at all. */}
            {home.description === '' &&
              home.photos.length === 0 &&
              home.training.length === 0 &&
              home.coaches.length === 0 &&
              home.tournaments.length === 0 && (
                <Paper sx={{ ...glowCard(), p: 4, textAlign: 'center' }}>
                  <GroupsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('club.empty')}
                  </Typography>
                </Paper>
              )}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
